import React from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import moment from 'moment-tz';
import styled from 'styled-components';

import {MultipleAssignedAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {ConfirmFormPopover} from 'app/general/widgets/ConfirmPopover';
import {ReassignTaskModal} from 'app/tasks/components/AssignTaskModal';
import {getRepetitionLabel, rruleFromString} from 'app/tasks/utils';
import {gt} from 'app/utils/i18n';

import {useCancelTaskTemplate} from '../api';

import type {TaskTemplateSerializedType} from 'sweet-types';

interface Props {
  task: TaskTemplateSerializedType;
  userTimezone: string;
}

export function TaskTemplateItem({task, userTimezone}: Props): JSX.Element {
  const {id, users, teams, roles, description, due, rrule} = task;
  const actionButtonRef = React.useRef<HTMLDivElement | null>(null);
  const [showReassign, setShowReassign] = React.useState(false);
  const [showCancel, setShowCancel] = React.useState(false);

  const [cancelTaskTemplate] = useCancelTaskTemplate();

  const rule = rruleFromString(rrule);
  const taskTime = moment(due).tz(userTimezone);

  const untilTime =
    rule.options.until != null
      ? moment(rule.options.until).tz(userTimezone)
      : null;

  const isRecurring = rule.options.count == null;
  const canceledBeforeDue = untilTime != null && untilTime.isBefore(taskTime);

  const assignees = React.useMemo(
    () => [...users, ...teams, ...roles],
    [users, teams, roles]
  );

  const commentsBadge =
    task.comment_count != null && task.comment_count > 0 ? (
      <span className="badge badge-light">
        <i className="far fa-comments" /> {task.comment_count}
      </span>
    ) : null;

  return (
    <>
      <div className="d-flex small clearfix p-2" id={`task${id}`}>
        <div className="d-flex flex-column mr-2">
          <MultipleAssignedAvatar
            tooltipTitle={gt('Assigned to')}
            className="float-left mb-2"
            tooltipPlacement="bottom"
            usersAndTeamsAndRoles={assignees}
          />
          {task.can_assign ? (
            <ActionsButton
              ref={actionButtonRef}
              actions={[
                {
                  text: isRecurring
                    ? gt('Edit or Reassign future tasks')
                    : gt('Edit or Reassign task'),
                  icon: 'fa fa-edit',
                  key: 'reassing',
                  callback: () => setShowReassign(true),
                },
              ].concat(
                untilTime != null
                  ? []
                  : [
                      {
                        text: isRecurring
                          ? gt('Cancel future tasks')
                          : gt('Cancel task'),
                        icon: 'fa fa-times',
                        key: 'cancel',
                        callback: () => setShowCancel(true),
                      },
                    ]
              )}
            />
          ) : null}
        </div>
        <MaybeLink html_url={task.html_url} shouldLink={!canceledBeforeDue}>
          {description != null || commentsBadge != null ? (
            <div className="font-weight-bold mb-1">
              {description} {commentsBadge}
            </div>
          ) : null}
          <div className="font-weight-light text-muted mb-1">
            <div className="mr-3">
              {gt('Repeats %s', getRepetitionLabel(rrule))}
              {untilTime != null ? (
                <span
                  className="ml-1 text-secondary"
                  title={
                    canceledBeforeDue
                      ? gt('Task was not started.', untilTime.format('lll'))
                      : isRecurring
                      ? gt('Task runs until %s', untilTime.format('lll'))
                      : gt(
                          'Task canceled after it was started',
                          untilTime.format('lll')
                        )
                  }
                >
                  &middot; {gt('Canceled')}
                </span>
              ) : null}
            </div>
          </div>

          <div className="text-muted font-italic">
            {isRecurring ? (
              <span title={gt('Task first due at %s', taskTime.format('lll'))}>
                {gt('Starts %s', taskTime.format('lll'))}
              </span>
            ) : (
              <span title={gt('Task due at %s', taskTime.format('lll'))}>
                {gt('Due at %s', taskTime.format('lll'))}
              </span>
            )}
            {untilTime != null ? (
              <>
                <br />
                <span title={gt('Task runs until %s', untilTime.format('lll'))}>
                  {gt('Until %s', untilTime.format('lll'))}
                </span>
              </>
            ) : null}
          </div>
        </MaybeLink>
      </div>
      {showReassign ? (
        <ReassignTaskModal
          applyToAllFutureInstances={true}
          show={showReassign}
          onToggle={() => setShowReassign(false)}
          taskTemplate={task}
        />
      ) : null}

      {showCancel && actionButtonRef.current != null ? (
        <ConfirmFormPopover
          overlayProps={{
            target: actionButtonRef.current,
            isOpen: showCancel,
            toggle: () => setShowCancel(false),
            placement: 'bottom',
          }}
          text={
            (task ? isRecurring : false)
              ? gt('Cancel all future tasks of this series?')
              : gt('Cancel this task?')
          }
          confirmText={gt('Yes')}
          busyText={gt('Cancelling...')}
          onConfirm={() =>
            cancelTaskTemplate({url: task.url, rrule: task.rrule}).unwrap()
          }
          isIdle={true}
        />
      ) : null}
    </>
  );
}

const StyledContainerFullHTMLLink = styled(FullHTMLLink)`
  display: block;
  flex-grow: 1;
  color: ${colors.textDefault};
`;

function MaybeLink({
  html_url,
  shouldLink,
  children,
}: {
  html_url: string;
  shouldLink: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return shouldLink ? (
    <StyledContainerFullHTMLLink html_url={html_url}>
      {children}
    </StyledContainerFullHTMLLink>
  ) : (
    <span>{children}</span>
  );
}
